import * as React from 'react';
import CountBadge from '@liveauctioneers/caterwaul-components/lib/CountBadge/CountBadge';
import EnableNotificationPrompt from './EnableNotificationPrompt';
import EnableNotifications from './EnableNotifications';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faBell as farBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faBell as fasBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import noop from '@/utils/noop';
import Notification from './Notification';
import notificationPermissions from '@/enums/notificationPermissions';
import Overlay from 'react-bootstrap/lib/Overlay';
import Popover from 'react-bootstrap/lib/Popover';
import styles from './NotificationDropDown.module.scss';

type DropDownMenuProps = {
    forceEnableNotificationsBanner: Function;
    markNotificationsDeleted: Function;
    markNotificationsRead: Function;
    mobile: boolean;
    notificationCount: number;
    notifications: any[];
    onEnter: (node: HTMLElement) => any;
    onEntered: (node: HTMLElement) => any;
    onExit: (node: HTMLElement) => any;
    onExited: (node: HTMLElement) => any;
    openMobileDownloadModal: Function;
    parent: any;
    permission: string;
    placement: string;
    requestBrowserPermission: Function;
    setPermission: Function;
    trackView: Function;
};

type DropDownMenuState = {
    hasTracked: boolean;
    show: boolean;
};

export default class NotificationDropDownMenu extends React.Component<DropDownMenuProps, DropDownMenuState> {
    static defaultProps = {
        notificationCount: 0,
        notifications: [],
        onEnter: noop,
        onEntered: noop,
        onExit: noop,
        onExited: noop,
        permission: notificationPermissions.DEFAULT,
    };

    state = { hasTracked: false, show: false };

    shouldComponentUpdate = (nextProps: DropDownMenuProps, nextState: DropDownMenuState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    targetRef: HTMLAnchorElement | null | undefined;

    open = () => {
        this.setState({ show: true });
        if (!this.state.hasTracked) {
            this.props.trackView();
            this.setState({ hasTracked: true });
        }
    };

    closeAndMarkRead = () => {
        if (this.props.notificationCount > 0) {
            this.markAllNotificationsAsRead();
        }
        this.close();
    };

    close = () => {
        this.setState({ show: false });
    };

    toggle = () => {
        if (this.state.show) {
            this.closeAndMarkRead();
        } else {
            this.open();
        }
    };

    markAllNotificationsAsRead = () => {
        const notificationIds = this.props.notifications.map((n) => n.id);
        this.props.markNotificationsRead(notificationIds);
    };

    markAllNotificationsAsDeleted = async () => {
        const notificationIds = this.props.notifications.map((n) => n.id);
        this.props.markNotificationsDeleted(notificationIds);
    };

    openModal = () => {
        this.props.openMobileDownloadModal();
    };

    render() {
        const {
            forceEnableNotificationsBanner,
            markNotificationsDeleted,
            markNotificationsRead,
            mobile,
            notificationCount,
            notifications,
            onEnter,
            onEntered,
            onExit,
            onExited,
            parent,
            permission,
            placement,
            requestBrowserPermission,
            setPermission,
        } = this.props;
        const hasNewNotifications = notificationCount > 0;
        const hasNotifications = notifications.length > 0;
        const iconStyles = hasNewNotifications ? styles.icon : styles['icon-none'];
        const markAllReadStyles = hasNotifications ? styles['delete-all'] : styles['delete-all-disabled'];
        const buttonStyles = styles.variantNotificationButton;
        return (
            <div className={buttonStyles}>
                <a onClick={this.toggle} ref={(ref) => (this.targetRef = ref)}>
                    <CountBadge className={styles.count} count={notificationCount} />
                    <div className={styles.menuItemVariant}>
                        <FontAwesomeIcon className={styles.expander} icon={faCaretDown} />
                        <FontAwesomeIcon className={iconStyles} icon={hasNewNotifications ? fasBell : farBell} />
                        <span className={styles.menuText}>
                            <FormattedMessage id="userMenu.notifications" />
                        </span>
                    </div>
                </a>
                <Overlay
                    // @ts-ignore replace bootstrap
                    className={styles.overlay}
                    container={parent}
                    onEnter={onEnter}
                    onEntered={onEntered}
                    onExit={onExit}
                    onExited={onExited}
                    onHide={this.closeAndMarkRead}
                    placement={placement}
                    rootClose
                    show={this.state.show}
                    target={this.targetRef}
                >
                    <Popover
                        className={styles.popover}
                        id="popover-trigger-click-root-close"
                        onMouseLeave={this.closeAndMarkRead}
                    >
                        <span className={styles['delete-all-span']}>
                            <Link className={markAllReadStyles} onClick={this.markAllNotificationsAsDeleted}>
                                <FormattedMessage id="notifications.delete_all" />
                            </Link>
                        </span>
                        <hr />
                        <EnableNotificationPrompt
                            forceEnableNotificationsBanner={forceEnableNotificationsBanner}
                            hasNotifications={hasNotifications}
                            permission={permission}
                            requestBrowserPermission={requestBrowserPermission}
                        />
                        {hasNotifications ? (
                            <div className={styles.notifications}>
                                {notifications.map((n, idx) => {
                                    return (
                                        <Notification
                                            id={n.id}
                                            imageUrl={n.imageUrl}
                                            isNew={idx < notificationCount}
                                            key={n.id}
                                            link={n.link}
                                            markNotificationsDeleted={markNotificationsDeleted}
                                            markNotificationsRead={markNotificationsRead}
                                            message={n.message}
                                            messageLevel={n.messageLevel}
                                            timestamp={n.timestamp}
                                            title={n.title}
                                        />
                                    );
                                })}
                            </div>
                        ) : (
                            <div>
                                <div className={styles['empty-state']}>
                                    <FormattedMessage id="notifications.no_new_notifications" />
                                </div>
                            </div>
                        )}
                        {mobile ? (
                            <div className={styles['download-link']}>
                                <FormattedMessage id="notifications.get_notifications_phone" />
                                <br />
                                <Link onClick={this.openModal}>
                                    <FormattedMessage id="notifications.download_app" />
                                </Link>
                            </div>
                        ) : (
                            <div className={styles['download-link']}>
                                <EnableNotifications
                                    forceEnableNotificationsBanner={forceEnableNotificationsBanner}
                                    permission={permission}
                                    requestBrowserPermission={requestBrowserPermission}
                                    setPermission={setPermission}
                                />
                            </div>
                        )}
                    </Popover>
                </Overlay>
            </div>
        );
    }
}
